import { makeStyles, Typography, Switch, FormLabel } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useContext } from 'react';
import authAPI, { Tenant } from '../../api/auth';
import { RadioboxGroup, SnackMessage } from '../../components/common';
import { InfoTooltip } from '../../components/common/InfoTooltip';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { COLORS } from '../../theme/variables';

type TenantKeyType = keyof Tenant;

interface SettingInputProps<T = boolean> {
  name: TenantKeyType;
  value: T;
  label: string;
  moduleName: string;
  handleChange: (key: TenantKeyType, value: T, moduleName: string) => void;
  disabled?: boolean;
  tooltip?: string;
}

interface RadioOption {
  label: string;
  value: string;
}

interface RadioProps extends SettingInputProps<string> {
  options: RadioOption[];
}

const useStyles = makeStyles({
  container: {
    maxWidth: 420,
  },
  title: {
    margin: '0 0 20px',
  },
  subtitle: {
    margin: '30px 0 20px',
  },
  toggleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  toggleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 5,
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  label: {
    color: COLORS.COLOR_TEXT_BASE,
  },
  radioWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
});

const Toggle: FC<SettingInputProps> = ({
  name,
  value,
  label,
  moduleName,
  disabled,
  tooltip,
  handleChange,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.toggleWrapper}>
      <div className={classes.labelContainer}>
        <FormLabel htmlFor={name} className={classes.label}>
          {label}
        </FormLabel>
        {tooltip && <InfoTooltip message={tooltip} />}
      </div>
      <Switch
        checked={value}
        color='primary'
        onChange={(_, checked) => handleChange(name, checked, moduleName)}
        disabled={disabled}
        id={name}
        name={name}
      />
    </div>
  );
};

const Radio: FC<RadioProps> = ({
  name,
  value,
  label,
  moduleName,
  options,
  disabled,
  tooltip,
  handleChange,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.radioWrapper}>
      <div className={classes.labelContainer}>
        <FormLabel htmlFor={name} className={classes.label}>
          {label}
        </FormLabel>
        {tooltip && <InfoTooltip message={tooltip} />}
      </div>
      <RadioboxGroup
        name={name}
        value={value}
        options={options}
        readOnly={disabled}
        onChange={(e) => handleChange(name, e.target.value, moduleName)}
        row
      />
    </div>
  );
};

function TenantAutomationPage() {
  const classes = useStyles();
  const { user, updateUser } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const { rb } = useResourceBundles();

  const handleChange = useCallback(
    async (key: TenantKeyType, value: boolean | string, moduleName: string) => {
      try {
        if (user) {
          const processedValue =
            typeof value === 'string' && key === 'agendaReminderDelay'
              ? Number(value)
              : value;
          const updatedTenant = await authAPI.updateTenant(user?.id, {
            ...user,
            [key]: processedValue,
            timeZone: user.timeZone ?? 'US/Eastern',
          });
          updateUser(updatedTenant);
          const message =
            typeof value === 'boolean'
              ? value
                ? `${moduleName} is enabled`
                : `${moduleName} is disabled`
              : `${moduleName} is set to ${value}`;
          enqueueSnackbar(message, {
            variant: 'success',
          });
        }
      } catch (e: any) {
        const messageError = e.response?.data?.message;
        const message = value
          ? `An error occurred while activating ${moduleName} module. Please, try again.`
          : `An error occurred while deactivating ${moduleName} module. Please, try again.`;

        enqueueSnackbar(message, {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, updateUser, user],
  );

  if (!user) return null;

  return (
    <div className={classes.container}>
      <Typography className={classes.title} variant='h4'>
        Automation Workflows
      </Typography>
      <Typography className={classes.subtitle} variant='h5'>
        Agenda Reminders
      </Typography>
      <div className={classes.toggleList}>
        <Toggle
          name='automationAgendaReminderEnabled'
          value={user.automationAgendaReminderEnabled}
          label={'Reminders for Next Week'}
          moduleName={'Agenda Reminders - Next Week'}
          handleChange={handleChange}
          tooltip={`Every Thursday we will send a friendly reminder to Founders about upcoming sessions for the next week and a need to create/update agenda for those sessions.`}
        />
        <Radio
          name='agendaReminderDelay'
          value={user.agendaReminderDelay?.toString()}
          label={'Min Days to Submit Agenda'}
          moduleName={'Min Days to Submit Agenda'}
          handleChange={handleChange}
          tooltip={`Set how many days before a session founders must submit their agenda.`}
          options={[
            { label: '2 days', value: '2' },
            { label: '4 days', value: '4' },
            { label: '7 days', value: '7' },
          ]}
        />
      </div>
      <Typography className={classes.subtitle} variant='h5'>
        RSVP Reminders
      </Typography>
      <div className={classes.toggleList}>
        <Toggle
          name='inviteReminderTwoDaysEnabled'
          value={!!user.inviteReminderTwoDaysEnabled}
          label={'Two Days Before'}
          moduleName={'RSVP Reminders - Two Days Before'}
          handleChange={handleChange}
          tooltip={`We will send reminders about session acceptance to all founders and ${rb(
            'mentors',
          )} invited to a session if they have not yet responded to the initial invite and the session is scheduled to take place in 48 hours.`}
        />
        <Toggle
          name='inviteReminderWeekEnabled'
          value={!!user.inviteReminderWeekEnabled}
          label={'One Week Before'}
          moduleName={'RSVP Reminders - One Week Before'}
          handleChange={handleChange}
          tooltip={`When this capability is enabled, our system will send invite reminders to all founders and ${rb(
            'mentors',
          )} who were invited to a session that was created a week ago and did not respond with either confirmation or decline of the session invite.`}
        />
      </div>
      <Typography className={classes.subtitle} variant='h5'>
        Other Automation
      </Typography>
      <div className={classes.toggleList}>
        <Toggle
          name='automaticSchedulingEnabled'
          value={!!user.automaticSchedulingEnabled}
          label={'Monthly Automated Scheduling'}
          moduleName={'Monthly Automated Scheduling'}
          handleChange={handleChange}
          tooltip={`If this option is enabled the system will find all the ventures that do not have upcoming events and will send out Scheduling Wizard invites to founders to initiate the process of scheduling. Automatic scheduling is done on the first day of each month.`}
        />
        <Toggle
          name='automationProfileUpdatesEnabled'
          value={user.automationProfileUpdatesEnabled}
          label={'Monthly Profile Update Request'}
          moduleName={'Monthly Profile Update Request'}
          handleChange={handleChange}
          tooltip={`We encourage founders and ${rb(
            'mentors',
          )} to keep their profiles up to date by sending update requests via e-mail. We will only reach out to those who do not have Additional Info section filled out. Founders and ${rb(
            'mentors',
          )} do not need Traction5 account to update their profiles.`}
        />
        <Toggle
          name='automationIssuesCleanupEnabled'
          value={user.automationIssuesCleanupEnabled}
          label={'Unresolved Issues Expiration'}
          moduleName={'Unresolved Issues Expiration'}
          handleChange={handleChange}
          tooltip={`In order to reduce the amount of workload on program admins we remove some of the Session issues automatically after certain period of time, when issue relevance is lost.`}
        />
      </div>
    </div>
  );
}

export default TenantAutomationPage;
